// React and Next.js imports
import React from "react";
import type { GetStaticProps, GetStaticPaths } from "next";

// Builder.io imports
import { BuilderContent, BuilderComponent, Builder } from "@builder.io/react";
import { SectionPage } from "@orlandohealth/build-kit";
import { SectionWrapper } from "@orlandohealth/build-kit";
import { logger } from "@orlandohealth/build-kit";
import type { IPhysician } from "@orlandohealth/types";
import {
  PhysicianSummary,
  PhysicianSchemaData,
  PhysicianProvider,
  type PhysicianAggregateRating,
} from "@orlandohealth/build-kit";
import {
  fetchSlugs,
  fetchPhysicianData,
  fetchPhysicianRating,
  fetchRegionalCareSiteId,
  resolveProviderVideoCutoutUrl,
  containsBuilderComponent,
} from "@orlandohealth/build-kit";

// Initialize a separate builder instance for the corp space
const corpBuilder = new Builder(
  process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY || ""
);

class PhysicianPage extends SectionPage {
  protected static renderContent(
    props: {
      physicianData: IPhysician;
      content?: any;
      aggregateRating?: PhysicianAggregateRating | null;
      hasEditableSummary?: boolean;
      hideOnlineSchedulingButton?: boolean;
      hideTalkToRepButton?: boolean;
      hideRequestAppointmentButton?: boolean;
    },
    model: string,
    _router: any,
    _navigationTitle?: string
  ) {
    const physicianData = props.physicianData as IPhysician;
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || "https://fl.orlandohealth.com";
    const pageUrl = `${siteUrl}/physician-finder/${physicianData.slug}`;
    const organizationId = `${siteUrl}/#organization`;
    return (
      <>
        <PhysicianSchemaData
          physician={physicianData}
          pageUrl={pageUrl}
          organizationId={organizationId}
          aggregateRating={props.aggregateRating}
        />
        <PhysicianProvider physicianData={physicianData}>
          <BuilderContent
            content={props.content || null}
            options={{ includeRefs: true, locale: _router?.locale || "Default" }}
            model={model}
          >
            {(_content: any, _loading?: boolean, fullContent?: any) => {
              const hasEditableSummary =
                props.hasEditableSummary ||
                containsBuilderComponent(
                  fullContent?.data?.blocks,
                  "EditablePhysicianSummary"
                );
              const hideOnlineSchedulingButton =
                (fullContent?.data?.hideOnlineSchedulingButton ?? props.hideOnlineSchedulingButton) === true;
              const hideTalkToRepButton =
                (fullContent?.data?.hideTalkToRepButton ?? props.hideTalkToRepButton) === true;
              const hideRequestAppointmentButton =
                (fullContent?.data?.hideRequestAppointmentButton ?? props.hideRequestAppointmentButton) === true;
              return (
                <>
                  {!hasEditableSummary && (
                    <PhysicianSummary
                      physicianData={physicianData}
                      hideOnlineSchedulingButton={hideOnlineSchedulingButton}
                      hideTalkToRepButton={hideTalkToRepButton}
                      hideRequestAppointmentButton={hideRequestAppointmentButton}
                    />
                  )}
                  <BuilderComponent
                    model={model}
                    content={fullContent}
                    options={{ includeRefs: true }}
                    locale={_router?.locale || "Default"}
                  />
                </>
              );
            }}
          </BuilderContent>
        </PhysicianProvider>
      </>
    );
  }
}

const Page = PhysicianPage.createPage(SectionWrapper, "provider");

export const getStaticPaths: GetStaticPaths = async ({
  locales = ["en_US"],
}) => {
  if (process.env.SKIP_SSG === "true") {
    return { paths: [], fallback: "blocking" };
  }
  const paths: { params: { slug: string[] }; locale: string }[] = [];

  try {
    const regionalCareSiteId = await fetchRegionalCareSiteId("fl");
    const slugs = await fetchSlugs(regionalCareSiteId);

    // Handle each locale
    for (const locale of locales) {
      // Add paths for each slug in each locale
      const localePaths = slugs.map((slug: string) => ({
        params: {
          slug: [slug],
        },
        locale,
      }));
      paths.push(...localePaths);
    }
  } catch (error) {
    logger.error(
      "Error fetching slugs in getStaticPaths:",
      error instanceof Error ? error.message : String(error)
    );
  }

  return {
    paths,
    // "blocking" rather than true. With `true`, slugs not prerendered
    // (physicians added since the last build) are answered from a
    // fallback shell — AbstractPage renders an empty <div> while
    // router.isFallback is set, so there is no <title>, canonical, og:*
    // or JSON-LD in the HTML. "blocking" server-renders the first
    // request so crawlers get a complete head.
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async ({
  params,
  locale,
  defaultLocale,
  preview: _preview,
}) => {
  try {
    const slug = (params?.slug as string[])?.[0];
    const urlPath = `/physician-finder/${
      Array.isArray(params?.slug) ? params.slug.join("/") : params?.slug || ""
    }`;

    const sharedProps = await SectionPage.getStaticProps(
      urlPath,
      slug,
      locale,
      defaultLocale,
      "provider",
      process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY
    );
    let physicianData;
    let providerContent = null;
    if (sharedProps.props.content != null) {
      providerContent = await corpBuilder.get("provider", {
        query: {
          id: sharedProps.props.content.id,
        },
        options: {
          noTargeting: true,
          includeRefs: true,
        },
        locale: locale || defaultLocale,
      });
      if (!providerContent) {
        throw new Error(`Provider content not found`);
      }

      physicianData = await fetchPhysicianData(slug);

      if (!physicianData) {
        throw new Error(`Failed to fetch physician data for slug: ${slug}`);
      }

      // Add title to the content
      providerContent.data = {
        ...providerContent.data,
        title: physicianData?.fullName,
      };

      if (providerContent.data?.bio) {
        physicianData.bio = providerContent.data.bio;
      }
      if (providerContent.data?.youtubeVideoId) {
        if (locale === "en") {
          physicianData.YoutubeId = providerContent.data.youtubeVideoId;
        } else {
          physicianData.YoutubeIdSpanish = providerContent.data.youtubeVideoId;
        }
      }
      let secondVideoUrl = "";
      if (locale === "es") {
        const englishContent = await corpBuilder.get("provider", {
          query: {
            id: sharedProps.props.content.id,
          },
          fields: "data.video,data.youtubeVideoId",
          locale: "en",
        });

        if (englishContent.data?.video) {
          const enUrl = resolveProviderVideoCutoutUrl(englishContent.data.video);
          if (enUrl) {
            secondVideoUrl = enUrl;
          }
        }
        if (
          englishContent.data?.youtubeVideoId &&
          providerContent.data?.youtubeVideoId !==
            englishContent.data.youtubeVideoId
        ) {
          physicianData.YoutubeId = englishContent.data.youtubeVideoId;
        }
      } else {
        const spanishContent = await corpBuilder.get("provider", {
          query: {
            id: sharedProps.props.content.id,
          },
          fields: "data.video,data.youtubeVideoId",
          locale: "es",
        });
        if (spanishContent.data?.video) {
          const esUrl = resolveProviderVideoCutoutUrl(spanishContent.data.video);
          logger.debug("spanishContent.data.video", esUrl ?? undefined);
          if (esUrl) {
            secondVideoUrl = esUrl;
          }
        }
        if (
          spanishContent.data?.youtubeVideoId &&
          providerContent.data?.youtubeVideoId !==
            spanishContent.data.youtubeVideoId
        ) {
          physicianData.YoutubeIdSpanish = spanishContent.data.youtubeVideoId;
        }
      }

      if (providerContent.data?.video != null) {
        const cutout = resolveProviderVideoCutoutUrl(providerContent.data.video);
        if (cutout !== null) {
          physicianData.CutoutVideoUrl = cutout;
        }
      }

      if (
        secondVideoUrl !== "" &&
        secondVideoUrl !== physicianData.CutoutVideoUrl
      ) {
        physicianData.SecondLanguageCutoutVideoUrl = secondVideoUrl;
      }
    } else {
      physicianData = await fetchPhysicianData(slug);

      // Create default Builder content structure with title
      providerContent = {
        data: {
          providerId: physicianData?.providerId,
          slug: slug,
          title: physicianData?.fullName, // Add title here
        },
        id: "default",
        name: physicianData?.fullName || "Default Provider",
        modelId: "provider",
      };
    }

    // getStaticProps props must be JSON-serializable; Hub/API may omit keys (undefined).
    if (physicianData.CutoutVideoUrl === undefined) {
      physicianData.CutoutVideoUrl = null;
    }
    if (physicianData.SecondLanguageCutoutVideoUrl === undefined) {
      physicianData.SecondLanguageCutoutVideoUrl = null;
    }

    const rating = await fetchPhysicianRating(physicianData?.npi);
    const aggregateRating = rating
      ? { ratingValue: rating.averageStarRatingStr, reviewCount: rating.reviewCount }
      : null;

    const hasEditableSummary = containsBuilderComponent(
      providerContent?.data?.blocks,
      "EditablePhysicianSummary"
    );

    const hideOnlineSchedulingButton = providerContent?.data?.hideOnlineSchedulingButton === true;
    const hideTalkToRepButton = providerContent?.data?.hideTalkToRepButton === true;
    const hideRequestAppointmentButton = providerContent?.data?.hideRequestAppointmentButton === true;

    return {
      props: {
        ...sharedProps.props,
        physicianData,
        content: providerContent,
        corpSectionData: providerContent,
        title: physicianData?.fullName, // Add title to props
        aggregateRating,
        hasEditableSummary,
        hideOnlineSchedulingButton,
        hideTalkToRepButton,
        hideRequestAppointmentButton,
      },
      revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10),
    };
  } catch (error) {
    // Improved error logging with more context
    logger.error("Error in getStaticProps:", "physician-finder", {
      error:
        error instanceof Error
          ? { message: error.message, stack: error.stack }
          : error,
      path: params?.slug,
      locale,
    });

    // Return notFound instead of redirect
    return {
      notFound: true,
      revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10),
    };
  }
};

export default Page;
